// Expand logical positions without changing backend cells or their metadata.
export function positionRows(rows = []) {
    const occupied = [];
    return rows.map((row, rowIndex) => {
        occupied[rowIndex] ||= [];
        let column = 0;
        return (Array.isArray(row) ? row : []).map(cell => {
            while (occupied[rowIndex][column]) column++;
            const colspan = Math.max(1, Number(cell.colspan) || 1);
            const rowspan = Math.max(1, Number(cell.rowspan) || 1);
            const entry = { cell, column, colspan };
            for (let r = rowIndex; r < rowIndex + rowspan; r++) {
                occupied[r] ||= [];
                for (let c = column; c < column + colspan; c++) occupied[r][c] = true;
            }
            column += colspan;
            return entry;
        });
    });
}

export function studyColumns(titles = []) {
    const rows = positionRows(titles);
    const width = Math.max(0, ...rows.flat().map(entry => entry.column + entry.colspan));
    return Array.from({ length: width }, (_, index) => {
        const path = rows.flat().filter(entry => index >= entry.column && index < entry.column + entry.colspan).map(entry => String(entry.cell.value ?? '').trim()).filter(Boolean);
        return { index, label: [...new Set(path)].join(' / ') || `Колонка ${index + 1}`, leaf: path[path.length - 1] || '' };
    });
}

const tenureHeaders = [
    { value: 'welcome пакет', kind: 'reward' },
    { value: 'Дата чз 3 месяца', months: 3 },
    { value: 'подарок 3 мес', kind: 'reward' },
    { value: 'Дата чз 6 месяца', months: 6 },
    { value: 'подарок 6 мес', kind: 'reward' },
    { value: 'Дата чз 12месяца', months: 12 },
    { value: 'подарок 12 мес', kind: 'reward' },
    { value: 'Дата чз 24 месяца', months: 24 },
    { value: 'подарок 24 мес', kind: 'reward' },
    { value: 'Дата чз 36 месяца', months: 36 },
    { value: 'подарок 36 мес', kind: 'reward' },
    { value: 'Дата чз 48 месяца', months: 48 },
    { value: 'подарок 48 мес', kind: 'reward' },
];

const addMonths = (value, months) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    if (!match) return '';
    const [, year, month, day] = match.map(Number);
    const targetMonth = month - 1 + months;
    const targetYear = year + Math.floor(targetMonth / 12);
    const normalizedMonth = targetMonth % 12;
    const lastDay = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
    const result = new Date(Date.UTC(targetYear, normalizedMonth, Math.min(day, lastDay)));
    return result.toISOString().slice(0, 10);
};

// The current study API response lacks the tenure block shown in the report
// design. Add its structure and derive milestone dates from the hire date.
export function withTenureSection(data = {}) {
    const titles = (data.titles || []).map(row => row.map(cell => ({ ...cell })));
    const existing = studyColumns(titles).some(column => /^(welcomeпакет|датачз3месяца)$/.test(normalize(column.leaf)));
    if (existing || !titles.length) return data;

    const width = studyColumns(titles).length;
    const firstRow = titles[0];
    firstRow.push({ id: 'tenure-group', value: 'Стаж работы', colspan: tenureHeaders.length });
    const lastRow = titles[titles.length - 1];
    lastRow.push(...tenureHeaders.map((header, index) => ({
        id: `tenure-${index + 1}`,
        value: header.value,
    })));

    const hireDateIndex = studyColumns(data.titles || []).findIndex(column => /^(датаприема|датаначалаработы)$/.test(normalize(column.leaf)));
    const rows = (data.rows || []).map(sourceRow => {
        const row = (sourceRow || []).map(cell => ({ ...cell }));
        const span = row.reduce((sum, cell) => sum + Math.max(1, Number(cell.colspan) || 1), 0);
        if (row.length === 1 && span >= width) {
            row[0].colspan = span + tenureHeaders.length;
            return row;
        }
        const hireDate = hireDateIndex >= 0 ? row[hireDateIndex] && row[hireDateIndex].value : '';
        row.push(...tenureHeaders.map((header, index) => ({
            id: `tenure-${index + 1}`,
            value: header.months ? addMonths(hireDate, header.months) : '',
            tenureReward: header.kind === 'reward',
        })));
        return row;
    });
    return { ...data, titles, rows };
}

const normalize = text => text.toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]/g, '');
export function studyColumnOrder(columns) {
    const keys = columns.map(column => normalize(column.leaf));
    const primary = [
        keys.findIndex(key => /^(фио|сотрудник)$/.test(key)),
        keys.findIndex(key => key === 'должность'),
        keys.findIndex(key => /^(др|датарождения)$/.test(key)),
        keys.findIndex(key => /^(датаприема|датаначалаработы)$/.test(key)),
    ].filter(index => index !== -1);
    const rest = columns.filter(column => !primary.includes(column.index) && !/^точк[аи]продаж[аи]?$/.test(keys[column.index])).map(column => column.index);
    return [...primary, ...rest];
}

export function projectStudyRows(rows = [], selected = []) {
    const visible = new Set(selected);
    const order = new Map(selected.map((column, index) => [column, index]));
    return positionRows(rows).map(row => row.slice().sort((a, b) => {
        const rank = entry => Math.min(...Array.from({ length: entry.colspan }, (_, offset) => order.get(entry.column + offset) ?? Infinity));
        return rank(a) - rank(b);
    }).flatMap(({ cell, column, colspan }) => {
        const count = Array.from({ length: colspan }, (_, offset) => column + offset).filter(index => visible.has(index)).length;
        return count ? [{ ...cell, colspan: count }] : [];
    }));
}

export function formatStudyValue(value) {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value !== 'string') return String(value);
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(value);
    if (!match) return value;
    const [, year, month, day] = match;
    if (+month < 1 || +month > 12 || +day < 1 || +day > new Date(+year, +month, 0).getDate()) return value;
    return `${day}.${month}.${year}`;
}
